import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Stack,
  Alert,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import useAuthorize from '../hooks/useAuthorize';
import { WHATSAPP_MODULE_ROLES } from '../roles/portalRoles';
import { fetchWhatsAppDashboardStats, WhatsAppDashboardStats } from './messageInboxApi';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DatasetComponent,
  TransformComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, subDays, parseISO } from 'date-fns';

// Register ECharts modules
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DatasetComponent,
  TransformComponent,
  LineChart,
  BarChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

const WhatsAppDashboard = () => {
  useAuthorize(WHATSAPP_MODULE_ROLES);
  const translate = useTranslate();

  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [stats, setStats] = useState<WhatsAppDashboardStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const loadStats = async (start: string, end: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWhatsAppDashboardStats(start, end);
      setStats(data);
    } catch (err: any) {
      console.error('Error loading dashboard stats:', err);
      setError(err.message || 'Failed to load dashboard statistics.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats(startDate, endDate);
  }, []);

  const handleFetchData = () => {
    loadStats(startDate, endDate);
  };

  const handlePreset = (days: number) => {
    const start = format(subDays(new Date(), days), 'yyyy-MM-dd');
    const end = format(new Date(), 'yyyy-MM-dd');
    setStartDate(start);
    setEndDate(end);
    loadStats(start, end);
  };

  // Render ECharts line chart
  useEffect(() => {
    if (!stats || stats.daily.length === 0 || !chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.setOption({
          series: [],
        });
      }
      return;
    }

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const dates = stats.daily.map((d) => {
      try {
        return format(parseISO(d.date), 'MMM dd');
      } catch (e) {
        return d.date;
      }
    });
    
    const sentData = stats.daily.map((d) => d.outbound);
    const deliveredData = stats.daily.map((d) => d.delivered + d.read);
    const readData = stats.daily.map((d) => d.read);

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985',
          },
        },
      },
      legend: {
        data: ['Outbound', 'Delivered', 'Read'],
        bottom: 0,
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '10%',
        top: '5%',
        containLabel: true,
      },
      xAxis: [
        {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLine: {
            lineStyle: {
              color: '#90a4ae',
            },
          },
        },
      ],
      yAxis: [
        {
          type: 'value',
          axisLine: {
            lineStyle: {
              color: '#90a4ae',
            },
          },
          splitLine: {
            lineStyle: {
              type: 'dashed',
              color: '#eceff1',
            },
          },
        },
      ],
      series: [
        {
          name: 'Outbound',
          type: 'line',
          smooth: true,
          lineStyle: {
            width: 3,
            color: '#2196f3',
          },
          itemStyle: {
            color: '#2196f3',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(33, 150, 243, 0.3)' },
              { offset: 1, color: 'rgba(33, 150, 243, 0)' },
            ]),
          },
          data: sentData,
        },
        {
          name: 'Delivered',
          type: 'line',
          smooth: true,
          lineStyle: {
            width: 3,
            color: '#4caf50',
          },
          itemStyle: {
            color: '#4caf50',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(76, 175, 80, 0.3)' },
              { offset: 1, color: 'rgba(76, 175, 80, 0)' },
            ]),
          },
          data: deliveredData,
        },
        {
          name: 'Read',
          type: 'line',
          smooth: true,
          lineStyle: {
            width: 3,
            color: '#009688',
          },
          itemStyle: {
            color: '#009688',
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(0, 150, 136, 0.3)' },
              { offset: 1, color: 'rgba(0, 150, 136, 0)' },
            ]),
          },
          data: readData,
        },
      ],
    };

    chartInstance.current.setOption(option);

    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.dispose();
        chartInstance.current = null;
      }
    };
  }, [stats]);

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const summary = stats?.summary || {
    total_inbound: 0,
    total_outbound: 0,
    total_sent: 0,
    total_delivered: 0,
    total_read: 0,
    total_failed: 0,
    total_undelivered: 0,
    total_contacts: 0,
    total_conversations: 0,
    total_customer_linked: 0,
    total_manual_replies: 0,
    total_ai_replies: 0,
  };

  const totalOutbound = summary.total_outbound || 0;
  const totalDelivered = (summary.total_delivered || 0) + (summary.total_read || 0);
  const totalRead = summary.total_read || 0;

  const deliveryRate =
    totalOutbound > 0
      ? ((totalDelivered / totalOutbound) * 100).toFixed(1)
      : '0.0';

  const readRate =
    totalOutbound > 0
      ? ((totalRead / totalOutbound) * 100).toFixed(1)
      : '0.0';

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'text.primary', mb: 3 }}>
        WhatsApp Sourcing Dashboard
      </Typography>

      {/* Date Range Selector & Controls */}
      <Paper sx={{ p: 2, mb: 4, borderRadius: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => handlePreset(0)}>Today</Button>
            <Button variant="outlined" onClick={() => handlePreset(1)}>Yesterday</Button>
            <Button variant="outlined" onClick={() => handlePreset(7)}>Last 7 Days</Button>
            <Button variant="outlined" onClick={() => handlePreset(30)}>Last 30 Days</Button>
          </Stack>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button variant="contained" onClick={handleFetchData} color="primary">
              Filter
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '5px solid #2196f3', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                Total Outbound
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                {totalOutbound}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '5px solid #4caf50', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                Delivered
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                {totalDelivered}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '5px solid #009688', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                Read
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#009688' }}>
                {totalRead}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '5px solid #ff9800', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                Delivery Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                {deliveryRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderLeft: '5px solid #9c27b0', borderRadius: 2 }}>
            <CardContent>
              <Typography color="textSecondary" variant="subtitle2" gutterBottom>
                Read Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                {readRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Charts & Pipeline */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 2, borderRadius: 2, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Outbound Message Trends
              </Typography>
              <div ref={chartRef} style={{ width: '100%', height: '350px' }} />
            </CardContent>
          </Card>
        </Grid>

        {/* Funnel Pipeline Widget */}
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 2, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Delivery Funnel
              </Typography>
              
              {/* Funnel Pipeline representation using visual blocks */}
              <Stack spacing={3} justifyContent="center" height="85%">
                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" color="textSecondary">1. Outbound</Typography>
                    <Typography variant="subtitle2" fontWeight="bold">100%</Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1, height: 28, bgcolor: 'rgba(33, 150, 243, 0.1)', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: '100%', bgcolor: '#2196f3' }} />
                      <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#fff', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {totalOutbound} messages
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" color="textSecondary">2. Delivered</Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="#4caf50">{deliveryRate}%</Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1, height: 28, bgcolor: 'rgba(76, 175, 80, 0.1)', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${deliveryRate}%`, bgcolor: '#4caf50' }} />
                      <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: parseFloat(deliveryRate) > 30 ? '#fff' : 'text.primary', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {totalDelivered} messages
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" color="textSecondary">3. Read</Typography>
                    <Typography variant="subtitle2" fontWeight="bold" color="#009688">{readRate}%</Typography>
                  </Stack>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1, height: 28, bgcolor: 'rgba(0, 150, 136, 0.1)', borderRadius: 1, position: 'relative', overflow: 'hidden' }}>
                      <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${readRate}%`, bgcolor: '#009688' }} />
                      <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: parseFloat(readRate) > 30 ? '#fff' : 'text.primary', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        {totalRead} messages
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Template Performance Table */}
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Template Performance
          </Typography>
          
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <Table aria-label="template performance table">
              <TableHead sx={{ bgcolor: '#f5f7f8' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Template ID</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Sent</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Delivered</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Delivery Rate (%)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Read</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Read Rate (%)</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Failed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats?.templates && stats.templates.length > 0 ? (
                  stats.templates.map((row) => {
                    const rowDeliveryRate = row.sent_count > 0 ? ((row.delivered_count / row.sent_count) * 100).toFixed(1) : '0.0';
                    const rowReadRate = row.sent_count > 0 ? ((row.read_count / row.sent_count) * 100).toFixed(1) : '0.0';
                    return (
                      <TableRow key={row.template_id} hover>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                          {row.template_id}
                        </TableCell>
                        <TableCell align="right">{row.sent_count}</TableCell>
                        <TableCell align="right">{row.delivered_count}</TableCell>
                        <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                          {rowDeliveryRate}%
                        </TableCell>
                        <TableCell align="right">{row.read_count}</TableCell>
                        <TableCell align="right" sx={{ color: '#009688', fontWeight: 'bold' }}>
                          {rowReadRate}%
                        </TableCell>
                        <TableCell align="right" sx={{ color: row.failed_count > 0 ? '#f44336' : 'inherit' }}>
                          {row.failed_count}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No templates tracked for this date range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default WhatsAppDashboard;
